import { ref, watch, onBeforeUnmount, computed } from 'vue';
import { useRepeat } from '@/worker/composables/useRepeat';
import { SlideViewType } from '@/common/components/organisms/alertListDetail/alertListDetail.types';
import { useAlertGroupStore } from '@/alert/stores/alert-group';
import { storeToRefs } from 'pinia';

export type PlatformType =
  | 'application'
  | 'awsDms'
  | 'awsDocumentDB'
  | 'awsDynamoDB'
  | 'awsEC2'
  | 'awsElasticache'
  | 'awsLambda'
  | 'awsRedshift'
  | 'awsS3'
  | 'azureBlobStorage'
  | 'azureCacheForRedis'
  | 'azureCosmosDB'
  | 'azureFunctions'
  | 'azureVirtualMachines'
  | 'business'
  | 'cluster'
  | 'container'
  | 'cronjob'
  | 'daemonset'
  | 'database'
  | 'database_cluster'
  | 'deployment'
  | 'gpu'
  | 'host'
  | 'hpa'
  | 'job'
  | 'kafka'
  | 'logs'
  | 'namespace'
  | 'networkDevice'
  | 'networkObject'
  | 'node'
  | 'persistentVolumeClaim'
  | 'pod'
  | 'replicaset'
  | 'route'
  | 'rum'
  | 'service'
  | 'statefulset'
  | 'url'
  | 'webserver'
  | 'persistentVolume'
  | 'storageClass';

export interface AlertInfoType {
  platformTypes?: PlatformType[];
  frameName: string;
  targetIds?: string[];
  type: SlideViewType;
}

export interface Props {
  alertInfo: AlertInfoType;
}

export const setup = (props: Props) => {
  const type = computed<SlideViewType>(() => props.alertInfo.type);
  const isShowAlertDetail = ref<boolean>(false);
  const alertCount = ref<number>(0);
  const alertGroupStore = useAlertGroupStore();

  const fetchAlertCount = async () => {
    const { platformTypes, frameName, targetIds } = props.alertInfo;

    try {
      await alertGroupStore.fetchAlertGroupList(frameName, platformTypes, targetIds);
      const { groupList } = storeToRefs(alertGroupStore);
      const criticalCount =
        groupList.value?.reduce((acc, item) => acc + (item.criticalCount ?? 0), 0) ?? 0;
      const warningCount =
        groupList.value?.reduce((acc, item) => acc + (item.warningCount ?? 0), 0) ?? 0;

      alertCount.value = criticalCount + warningCount;
    } catch (e) {
      console.log(e);
    }
  };

  const { resetFetch, clearFetch } = useRepeat(fetchAlertCount, 5_000, {
    isSetup: false,
    isImmediate: false,
  });

  const onClickAlert = () => {
    isShowAlertDetail.value = true;
  };

  watch(
    () => isShowAlertDetail.value,
    () => {
      resetFetch();
    },
    { immediate: true },
  );

  watch(
    () => props.alertInfo.targetIds,
    (targetIds) => {
      if (targetIds?.length) {
        resetFetch();
      }
    },
  );

  onBeforeUnmount(() => {
    clearFetch();
    alertGroupStore.reset();
  });

  return {
    isShowAlertDetail,
    alertCount,
    onClickAlert,
    type,
  };
};
